import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers';
import GrantCard from '@hellodarwin/core/lib/components/grants/card';
import { getButtonCta } from '@hellodarwin/core/lib/components/grants/funding-explorer/content/utils/getButtonCta';
import { GrantResult } from '@hellodarwin/core/lib/features/entities/grants-entities';
import {
  ClientProgramStatus,
  Program,
  ProgramGrantStatus,
  ProgramSource,
  ProgramStatus,
  ProgramTargetUser,
} from '@hellodarwin/core/lib/features/entities/programs-entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/app-hooks';
import { useClientAnalytics } from '../../features/analytics/use-client-analytics';
import { subscribeNotifications } from '../../features/api/slices/grants-notifications-slice';
import { selectCreateProgramLoading } from '../../features/api/slices/grants-slice';
import {
  fetchCompany,
  fetchContact,
  selectCompany,
  selectContact,
} from '../../features/api/slices/profile-slice';
import {
  createProgram,
  fetchGrantForProgram,
  GetRoadmapClientPrograms,
  GetRoadmapPrograms,
  selectAllRoadmapClientPrograms,
  selectAllRoadmapPrograms,
  selectGrantById,
} from '../../features/api/slices/programs-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../../features/api/use-client-api';

type AssistantGrantCardProps = {
  grantId: string;
};

const AssistantGrantCard = ({ grantId }: AssistantGrantCardProps) => {
  const dispatch = useAppDispatch();
  const api = useClientApi();
  const newApi = useNewClientApi();
  const { selectedLocale } = useLocale();
  const company = useAppSelector(selectCompany);
  const contact = useAppSelector(selectContact);
  const analytics = useClientAnalytics();
  const navigate = useNavigate();
  const programs = useAppSelector(selectAllRoadmapPrograms);
  const clientPrograms = useAppSelector(selectAllRoadmapClientPrograms);
  const { accessLevel } = usePremiumAccess();
  const isExpertAccess = accessLevel === 'expert';
  const createProgramLoading = useAppSelector(selectCreateProgramLoading);

  const grant = useAppSelector((state) => selectGrantById(state, grantId));
  const program = isExpertAccess
    ? undefined
    : accessLevel === 'custom' || accessLevel === 'admin'
      ? programs.find((program) => program.program_grant_id === grant?.grant_id)
      : clientPrograms.find(
          (program) => program.program_grant_id === grant?.grant_id,
        );

  const [cta, setCta] = useState(() =>
    getButtonCta(t, program?.client_program_status, accessLevel, !!program),
  );

  useEffect(() => {
    setCta(
      getButtonCta(t, program?.client_program_status, accessLevel, !!program),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.client_program_status, accessLevel]);

  useEffect(() => {
    if (isExpertAccess) return;

    dispatch(GetRoadmapPrograms({ api, locale: selectedLocale }));
    dispatch(GetRoadmapClientPrograms({ api, locale: selectedLocale }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocale, cta, isExpertAccess]);

  const handleClickCta = async (grant: GrantResult) => {
    if (isExpertAccess) {
      navigate(`/funding-explorer/${grant.grant_id}`);
      return;
    }

    analytics.grantApplyNowClicked({ clicked_grant_id: grant.grant_id });
    if (!program) {
      createNewProgram(grant);
      if (!grant.grant_is_subscribed) {
        subscribe(grant, true);
      }
    } else {
      navigate(`/applications/${program.program_id}`);
    }
  };

  const createNewProgram = async (grant: GrantResult) => {
    if (isExpertAccess) return;

    const program: Program = {
      program_id: '',
      program_name: `${grant.grant_display_title} | ${company?.name} | ${new Date().getFullYear()}`,
      program_contact_id: contact?.contact_id,
      program_company_id: company?.company_id,
      program_grant_id: grant.grant_id,
      program_account_manager: company?.account_manager,
      program_status: ProgramStatus.Active,
      program_source: ProgramSource.Client,
      client_program_status:
        ClientProgramStatus.ProjectValidationAndEligibility,
      program_grant_status: ProgramGrantStatus.Identified,
      program_target_user:
        accessLevel === 'custom' || accessLevel === 'admin'
          ? ProgramTargetUser.Admin
          : ProgramTargetUser.Client,
    };
    try {
      await dispatch(createProgram({ api: newApi, program })).unwrap();
      antdMessage.success(`${t('grants|applicationCreatedSuccess')}`);
      setCta(
        getButtonCta(
          t,
          ClientProgramStatus.ProjectValidationAndEligibility,
          accessLevel,
          true,
        ),
      );
    } catch (error) {
      console.error(error);
      antdMessage.error(`${t('grants|applicationCreatedFailed')}`);
    }
  };

  const subscribe = async (grant: GrantResult, silent: boolean = false) => {
    if (isExpertAccess) return;

    try {
      await dispatch(
        subscribeNotifications({
          api: newApi,
          request: {
            grant_ids: [grant.grant_id],
            industry_ids: [],
            tag_ids: [],
          },
        }),
      ).unwrap();
      if (!silent) {
        antdMessage.success(
          t('grantNotificationJourneys|subscriptionCreateSuccess'),
        );
      }
    } catch (_) {
      antdMessage.error(t('grantNotificationJourneys|subscriptionCreateFail'));
    }
  };

  useEffect(() => {
    if (!grant) {
      dispatch(
        fetchGrantForProgram({
          api,
          grantId,
          locale: selectedLocale,
          programId: program?.program_id || '',
        }),
      );
    }
  }, [grantId, grant, dispatch, api, selectedLocale, program]);

  useEffect(() => {
    if (isExpertAccess) return;

    if (!company) {
      dispatch(fetchCompany({ api }));
    }
    if (!contact) {
      dispatch(fetchContact({ api, syncSendGrid: false }));
    }
  }, [dispatch, api, company, contact, isExpertAccess]);

  return grant ? (
    <GrantCard
      type="preview"
      {...grant}
      parentPath="/funding-explorer"
      ctaText={cta.buttonCta}
      buttonVariant={cta.variant}
      createProgramLoading={
        isExpertAccess ? false : createProgramLoading?.[grant.grant_id] || false
      }
      handleClickCta={handleClickCta}
    />
  ) : null;
};

export default AssistantGrantCard;
